import { ref, watch, onUnmounted } from 'vue';
import { getCommentStatus } from '@/js/core/utils/trays-api';

const POLL_INTERVAL_MS = 30000;

/**
 * Format date as MySQL datetime string (YYYY-MM-DD HH:MM:SS) in UTC
 */
function toMySQLDatetime(date) {
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * Composable for polling comment status to detect new comments
 * @param {import('vue').Ref<string|null>} trayUuid - Reactive tray UUID
 */
export function useCommentPolling(trayUuid) {
  const hasNewComments = ref(false);
  const newCommentCount = ref(0);
  const lastCheckedAt = ref(null);

  let pollIntervalId = null;

  async function checkForNewComments() {
    if (!trayUuid.value || !lastCheckedAt.value) return;

    try {
      const response = await getCommentStatus(trayUuid.value, lastCheckedAt.value);
      const status = response.data || {};

      if (status.new_comment_uuids?.length > 0) {
        hasNewComments.value = true;
        newCommentCount.value = status.new_comment_uuids.length;
      }
    } catch {
      // Silently ignore errors, polling will continue
    }
  }

  function start() {
    stop();
    lastCheckedAt.value = toMySQLDatetime(new Date());
    hasNewComments.value = false;
    newCommentCount.value = 0;

    pollIntervalId = setInterval(checkForNewComments, POLL_INTERVAL_MS);
  }

  function stop() {
    if (pollIntervalId) {
      clearInterval(pollIntervalId);
      pollIntervalId = null;
    }
  }

  function markRefreshed() {
    hasNewComments.value = false;
    newCommentCount.value = 0;
    lastCheckedAt.value = toMySQLDatetime(new Date());
  }

  // Restart polling when trayUuid changes (SPA navigation)
  watch(trayUuid, newUuid => {
    if (newUuid) {
      start();
    } else {
      stop();
    }
  });

  // Auto-stop on unmount
  onUnmounted(() => {
    stop();
  });

  return {
    hasNewComments,
    newCommentCount,
    start,
    stop,
    markRefreshed,
  };
}
